// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/brushPackClient.js
//
// Runs pack inspection in a worker, falling back to the main thread where
// module workers are unavailable. Cancellation works either way.
import { inspectBrushFiles, MAX_FILE_BYTES } from './packInspector.js';

let worker = null;
let workerBroken = false;
let nextId = 1;

function getWorker() {
  if (workerBroken || typeof Worker === 'undefined') return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./brushPackWorker.js', import.meta.url));
    worker.onerror = () => {
      workerBroken = true;
      worker = null;
    };
    return worker;
  } catch {
    workerBroken = true;
    return null;
  }
}

async function readFiles(files, signal) {
  const out = [];
  for (const file of files) {
    if (signal && signal.aborted) break;
    if (file.size > MAX_FILE_BYTES) {
      out.push({ name: file.name, bytes: new Uint8Array(0), tooLarge: true });
      continue;
    }
    out.push({ name: file.name, bytes: new Uint8Array(await file.arrayBuffer()) });
  }
  return out;
}

function abortError() {
  const error = new Error('Import cancelled');
  error.name = 'AbortError';
  return error;
}

export async function inspectFiles(fileList, { signal, onProgress } = {}) {
  const files = Array.from(fileList || []).filter(Boolean);
  if (typeof onProgress === 'function') onProgress({ phase: 'read', index: 0, total: files.length });
  const read = await readFiles(files, signal);
  if (signal && signal.aborted) throw abortError();
  const tooLarge = read.filter((file) => file.tooLarge).map((file) => ({ fileName: file.name, message: `larger than ${Math.round(MAX_FILE_BYTES / 1048576)} MB` }));
  const usable = read.filter((file) => !file.tooLarge);
  const instance = getWorker();
  if (!instance) {
    const result = await inspectBrushFiles(usable, { signal, onProgress });
    result.failures = [...tooLarge, ...result.failures];
    return result;
  }
  const id = nextId++;
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      instance.postMessage({ type: 'cancel', id });
      cleanup();
      reject(abortError());
    };
    const onMessage = (event) => {
      const message = event.data || {};
      if (message.id !== id) return;
      if (message.type === 'progress' && typeof onProgress === 'function') onProgress(message.progress);
      if (message.type === 'result') {
        cleanup();
        message.result.failures = [...tooLarge, ...message.result.failures];
        resolve(message.result);
      }
      if (message.type === 'error') {
        cleanup();
        const error = new Error(message.message);
        error.name = message.name || 'Error';
        reject(error);
      }
    };
    function cleanup() {
      instance.removeEventListener('message', onMessage);
      if (signal) signal.removeEventListener('abort', onAbort);
    }
    instance.addEventListener('message', onMessage);
    if (signal) signal.addEventListener('abort', onAbort);
    instance.postMessage({ type: 'inspect', id, files: usable }, usable.map((file) => file.bytes.buffer));
  });
}
